import { removePathsFromError, stringifyError } from 'shared/functions';
import type { SkillTaxonomyDto, SkillTaxonomySection, SkillTaxonomyTreeDto } from '@eva/types/dto';
import PageSpinner from 'shared/ui/Spinner/PageSpinner';

import { SkillTaxonomyTreeItem } from 'entities/skillTaxonomy/SkillTaxonomyTreeItem';

import type { UseQueryResult } from 'react-query/types/react/types';

import { getSkillTaxonomyById } from '../../../entities/skillTaxonomy/functions';

import { useFetchSkillTaxonomies } from '../hooks/useFetchSkillTaxonomies';

type SkillTaxonomiesListType = {
  skillTaxonomies: SkillTaxonomySection[];
  hooks: {
    useFetchSkillTaxonomyOptions: () => UseQueryResult<{ skillTaxonomies: SkillTaxonomyDto[] }>;
    useFetchSkillTaxonomyTree: (skillTaxonomyId: number) => UseQueryResult<SkillTaxonomyTreeDto>;
  };
  highlightSkillLabelMemoized?: (skill: any) => void;
  defaultExpandedSkills?: number[];
};

// * prop skillTaxonomies consist only of skillTaxonomies that have skills
export const SkillTaxonomiesList: React.FC<SkillTaxonomiesListType> = ({
  skillTaxonomies,
  highlightSkillLabelMemoized,
  hooks,
  defaultExpandedSkills,
}) => {
  // * useFetchSkillTaxonomyOptions return the skillTaxonomies allowed for the current entity
  const { data, isLoading, isError, error } = useFetchSkillTaxonomies(hooks.useFetchSkillTaxonomyOptions);
  if (isLoading) {
    return <PageSpinner />;
  }

  if (isError) {
    const errorWithoutPath = removePathsFromError(error);
    return <h1>{stringifyError(errorWithoutPath)}</h1>;
  }

  return (
    <>
      {data &&
        data.skillTaxonomies &&
        data.skillTaxonomies.map((item) => {
          const skillTaxonomySection = getSkillTaxonomyById(skillTaxonomies, item.skillTaxonomyId);
          if (!skillTaxonomySection?.skills.length) return null;

          return (
            <SkillTaxonomyTreeItem
              key={item.skillTaxonomyId}
              skills={skillTaxonomySection?.skills || []}
              note={skillTaxonomySection?.note || ''}
              name={item.name}
              skillProficiencies={item.proficiencies}
              highlightSkillLabelMemoized={highlightSkillLabelMemoized}
              useFetchSkillTaxonomyTree={() => hooks.useFetchSkillTaxonomyTree(item.skillTaxonomyId)}
              defaultExpandedSkills={defaultExpandedSkills}
            />
          );
        })}
    </>
  );
};
